'use client'

import { useState } from 'react'
import {
  useUpdateDocumentState,
  DocumentSearchResult,
  useGetOriginalPdf,
} from '@dc-shared-web/hooks/api/document'
import { useAuth } from '@dc-shared-web/providers/AuthProvider'
import TableActionMenu, {
  BaseMenuItem,
} from '@/app/core/components/TableActionMenu'
import {
  ShareActionType,
  ShareDialog,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import {
  userHasExplicitPermission,
  hasMovePrivileges,
} from '@/app/helpers/userHelper'
import { RenameDocumentDialog } from '@/app/search/RenameDocumentDialog'
import { DeleteDocumentDialog } from '@/app/search/DeleteDocumentDialog'
import { MoveDocumentDialog } from '@/app/search/MoveDocumentDialog'
import { useQueryClient } from 'react-query'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import { createDownloadHandlers } from '@/app/helpers/downloadOriginalPdf'
import { UploadVersionModal } from '@/app/(dashboard)/upload-version/UploadVersionModal'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'

interface MenuItem extends BaseMenuItem<CrunchesTableActionType> {
  action: (document: DocumentSearchResult) => void
  openDialog: boolean
}

export type CrunchesTableActionType =
  | 'Share'
  | 'Rename'
  | 'Move'
  | 'Archive'
  | 'Compare versions'
  | 'Unarchive'
  | 'Delete'
  | 'Download'

export function CrunchesTableActionMenu({
  document,
  canMoveCrunches,
  showUploadVersion,
  uploadVersionDisabled,
  uploadVersionTooltip,
}: {
  document: DocumentSearchResult
  canMoveCrunches: boolean
  showUploadVersion: boolean
  uploadVersionDisabled: boolean
  uploadVersionTooltip: string | undefined
}) {
  const { user } = useAuth()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [uploadVersionDialogOpen, setUploadVersionDialogOpen] = useState(false)
  const [actionType, setActionType] = useState<
    CrunchesTableActionType | undefined
  >(undefined)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { mutate: downloadOriginalPdf } = useGetOriginalPdf()

  const isTeamEditor = (teamUUID: string) => {
    return userHasExplicitPermission(user, {
      resource_uuid: teamUUID,
      role: 'EDITOR',
    })
  }

  const hasArchivePrivileges = () => {
    return !isTeamEditor(document.teamUUID)
  }

  const hasDeletePrivileges = () => {
    return !isTeamEditor(document.teamUUID)
  }

  const hasDownloadPrivileges = () => {
    return !document.isArchived && user.is_super_admin
  }

  const handleMenuItemClick = (menuItem: MenuItem) => {
    menuItem.action(document)
    setActionType(menuItem.text)
    setDialogOpen(menuItem.openDialog)
  }

  const onShareSuccess = (action: ShareActionType) => {
    if (action === 'SHARE') {
      setShareDialogOpen(false)
    }
  }

  const onActionSuccess = async () => {
    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: ['folders', 'list'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['projects', 'list'],
      }),
    ])

    setDialogOpen(false)
  }

  const { mutate: updateDocumentArchivedState } = useUpdateDocumentState({
    documentId: document.uuid,
  })
  const { trackEvent } = useFSTrackEvent()

  const menuItems: MenuItem[] = [
    {
      text: 'Share',
      action: () => {
        setShareDialogOpen(true)
      },
      hidden: !!document.isArchived,
      openDialog: false,
    },
    {
      text: 'Rename',
      action: () => {},
      hidden: !!document.isArchived,
      openDialog: true,
    },
    {
      text: 'Move',
      action: () => {},
      hidden:
        !hasMovePrivileges({ user, document }) ||
        !!document.isArchived ||
        !canMoveCrunches,
      openDialog: true,
    },
    {
      text: 'Compare versions',
      action: () => {
        trackEvent(FS.BUTTON_CLICKED, {
          name: 'Compare versions',
          page_category: FS_CATEGORY.DASHBOARD,
          entity_id: document.uuid,
          entity_type: 'crunch',
        })
        setUploadVersionDialogOpen(true)
      },
      hidden: !showUploadVersion,
      disabled: uploadVersionDisabled,
      disabledTooltip: uploadVersionTooltip,
      openDialog: false,
    },
    {
      text: document.isArchived ? 'Unarchive' : 'Archive',
      action: () => {
        updateDocumentArchivedState({
          uuid: document._shortenedUuid,
          state: document.isArchived ? 'unarchive' : 'archive',
          name: document.fileName,
        })
      },
      hidden: !hasArchivePrivileges(),
      openDialog: false,
    },
    {
      text: 'Delete',
      action: () => {},
      hidden: !hasDeletePrivileges(),
      openDialog: true,
    },
    {
      text: 'Download',
      action: (document: DocumentSearchResult) => {
        downloadOriginalPdf(
          { documentUUID: document.documentGuid },
          createDownloadHandlers(document.fileName, toast)
        )
      },
      hidden: !hasDownloadPrivileges(),
      openDialog: false,
    },
  ]

  return (
    <>
      <TableActionMenu
        data-testid='dashboard'
        items={menuItems}
        onSelect={handleMenuItemClick}
      />
      {shareDialogOpen ? (
        <ShareDialog
          open={shareDialogOpen}
          setOpen={setShareDialogOpen}
          document={{
            id: document.uuid,
            name: document.fileName,
            team: document.teamName,
            teamId: document.teamUUID,
          }}
          onActionSuccess={onShareSuccess}
        />
      ) : null}
      {actionType === 'Rename' ? (
        <RenameDocumentDialog
          document={document}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      ) : null}
      {actionType === 'Move' ? (
        <MoveDocumentDialog
          variant='crunch'
          document={document}
          open={dialogOpen}
          setOpen={setDialogOpen}
          onSuccess={onActionSuccess}
        />
      ) : null}
      {actionType === 'Delete' ? (
        <DeleteDocumentDialog
          document={document}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={onActionSuccess}
        />
      ) : null}
      {uploadVersionDialogOpen ? (
        <UploadVersionModal
          onClose={() => setUploadVersionDialogOpen(false)}
          document={document}
        />
      ) : null}
    </>
  )
}
